import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './idb-publications-card.css.lit';
import { classMap } from 'lit/directives/class-map.js';
import fitty from 'fitty';
import { OutlineElement } from 'src/components/base/outline-element/outline-element';
import { LinkTargetType } from 'src/components/base/outline-link/config';
import { setDateLangShortFormat } from '../../../utilities/date-lang-format';

export type AllowedColors =
  | 'gray'
  | 'light-blue'
  | 'blue'
  | 'green'
  | 'white'
  | 'transparent';
export type TagBackgrounds = 'yellow' | 'green' | 'orange' | 'purple';

/**
 * The IDB Publications Card component.
 * @element idb-publications-card
 * @extends OutlineElement
 * @see Events Card for similar styling
 * @slot tags - the tags slot
 * @slot image - The image slot.
 * @slot summary - The summary slot.
 * @slot title - The title slot.
 * @slot date - The date slot.
 */
@customElement('idb-publications-card')
export class IdbPublicationsCard extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * The color class to apply to the background.
   */
  @property({ type: String, attribute: 'card-background' })
  cardBackground: AllowedColors;

  // URL for the card to link too
  @property({ type: String, reflect: true, attribute: 'url' }) url: string;

  /**
   * The url for the image.
   */
  @property({ type: String, attribute: 'image-url' })
  imageUrl: string;

  /**
   * The alt text for the image.
   */
  @property({ type: String, attribute: 'image-alt' })
  imageAlt: string;

  /**
   * The link target attribute
   */
  @property({ type: String, reflect: true, attribute: 'link-target' })
  linkTarget: LinkTargetType = '_blank';

  /**
   * The date lang attribute
   */
  @property({ type: String, attribute: 'date-lang' })
  dateLang: string;

  /**
   * The date attribute
   */
  @property({ type: String, attribute: 'date' })
  date: string;

  connectedCallback() {
    super.connectedCallback();
  }

  firstUpdated(): void {
    const fitText = (this.shadowRoot as ShadowRoot).querySelector(
      '.fitty-wrapper'
    );
    if (fitText !== null) {
      fitty(fitText as HTMLElement, {
        minSize: 20,
        maxSize: 60,
        multiLine: true,
      });
    }
  }

  render(): TemplateResult {
    const cardClasses = {
      card: true,
      [`outline-bg-${this.cardBackground}`]: this.cardBackground !== undefined,
    };
    const imageClasses = {
      'card-image': true,
    };
    const contentWrapperClasses = {
      'card-content-wrapper': true,
    };
    const titleClasses = {
      'card-title': true,
      'fitty': true,
    };
    const summaryClasses = {
      'card-summary': true,
      'clamp': true,
    };
    const dateClasses = {
      'card-date': true,
    };
    const dateStringClasses = {
      'card-date-string': true,
    };

    return html`
      <a href="${this.url}" target="${this.linkTarget}">
        <section class="${classMap(cardClasses)}">
        <picture class="${classMap(imageClasses)}">
          <img alt="${this.imageAlt}" src="${this.imageUrl}"/>
        </picture>
        <section class="${classMap(contentWrapperClasses)}">
          <div class="image-tag-wrapper">
            <slot name="image-tags"></slot>
          </div>
          <div class="${classMap(titleClasses)}">
              <slot name="title"></slot>
          </div>
          <div class="${classMap(summaryClasses)}">
            <slot name="summary"></slot>
          </div>
          <div class="${classMap(dateClasses)}">
            <div class="${classMap(
              dateStringClasses
            )}">${setDateLangShortFormat(this.date, this.dateLang)}</div>
          </div>
          </section>
        </section>
      </a>
    `;
  }
}
